import { Component, DestroyRef, model, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { forkJoin, map } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { OrderDetail, Order, ColorShots, OrderStatus } from 'src/app/components/company/models/new/order';
import { ShotApprovalStatus, Shot } from 'src/app/components/company/models/new/shot';
import { OrderMapper } from 'src/app/components/company/services/order-mapper';
import { DatabaseQueries, OrderQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { ImageService } from 'src/app/services/image.service';
import { ConfirmModalComponent } from 'src/app/shared/components/confirm-modal/confirm-modal.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { ImagePreviewModalComponent } from './image-preview-modal/image-preview-modal.component';
import { UploadMultipleModalComponent } from './upload-multiple-modal/upload-multiple-modal.component';
import { CommonService } from 'src/app/services/common.service';
import * as _ from 'lodash';
import { ExcelService } from 'src/app/services/excel.service';
import { ImageGalleryComponent } from 'src/app/shared/components/image-gallery/image-gallery.component';
import { UploadedImageShot } from '../../../models/new/imageShot';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { Warehouse } from 'src/app/shared/types/warehouse.model';
import { PaginationFilters } from 'src/app/components/company/models/new/paginationFilters';
import { Paginable } from 'src/app/shared/types/paginable';
import { ShotResume } from 'src/app/components/company/models/new/shotResume';
import { ResumeMapper } from 'src/app/components/company/services/resume-mapper';
import { UploadMultipleWithOrchestratorModalComponent } from './upload-multiple-with-orchestrator-modal/upload-multiple-with-orchestrator-modal.component';
import { UploadValidationResultsModalComponent } from './upload-multiple-with-orchestrator-modal/upload-validation-results-modal/upload-validation-results-modal.component';
import { PickupModalComponent } from './pickup-modal/pickup-modal.component';

@Component({
  selector: 'bt-photographer-order-detail-shots',
  standalone: false,
  templateUrl: './photographer-order-detail-shots.html',
  styleUrl: './photographer-order-detail-shots.scss'
})
export class PhotographerOrderDetailShotsComponent implements OnInit {
  loading: boolean = false;
  currentWarehouse: Warehouse | null = null;
  ShotApprovalStatus = ShotApprovalStatus;
  OrderStatus = OrderStatus;

  orderDetails: Paginable<Selectable<OrderDetail>> = new Paginable<Selectable<OrderDetail>>();
  order: Order | null = null;
  orderDetailsFilters = new PaginationFilters();
  shotResume: ShotResume = new ShotResume();

  acceptedExtensions = ['.jpg', '.jpeg', '.png', '.mkv', '.mp4'];

  constructor(
    private route: ActivatedRoute,
    private appService: AppService,
    private graphQLService: GraphQLService,
    private imageService: ImageService,
    private utilitiesService: UtilitiesService,
    private commonService: CommonService,
    private dialog: MatDialog,
    private orderMapper: OrderMapper,
    private excelService: ExcelService,
    private resumeMapper: ResumeMapper,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.parent?.params.subscribe((params) => {
      const orderId = params['orderId'];

      if (orderId != 'new') {
        this.getOrderInfo(Number(orderId));
      }
    });
  }

  pageEvent(event: { pageNumber: number; pageSize: number }) {
    this.orderDetails.currentPage = event.pageNumber;
    this.orderDetails.size = event.pageSize;

    this.orderDetailsFilters.page = event.pageNumber;
    this.orderDetailsFilters.size = event.pageSize;

    this.refreshNavigation();
  }

  refreshNavigation() {
    this.loading = true;

    this.router
      .navigate([location.pathname], {
        queryParams: { page: this.orderDetailsFilters.page, size: this.orderDetailsFilters.size }
      })
      .then((navigated) => {
        // Stessi filtri = stesso URL: il router ignora la navigazione e la lista non si ricarica
        if (!navigated) {
          this.loading = false;
        }
      });
  }

  getOrderInfo(_orderId: number): void {
    this.loading = true;
    this.graphQLService
      .getData(OrderQueries.GET_ORDERS, { where: { orderId: { eq: _orderId } }, first: 1 })
      .pipe(map((res: any) => this.orderMapper.toOrder(res.data.orders.nodes[0])))
      .subscribe({
        next: (data: Order) => {
          this.order = data;

          if (this.order.warehouse) {
            this.currentWarehouse = _.cloneDeep(this.order.warehouse);
          }

          if (this.order?.status?.id == OrderStatus.ReadyForPickup) {
            this.dialog.open(PickupModalComponent, {
              data: { warehouse: this.currentWarehouse, isFake: this.order?.isFake }
            });
          }

          this.getOrderDetails(Number(this.order?.id));
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error during fetching order info', SnackbarType.Error);
          console.error(err);
          this.loading = false;
        }
      });
  }

  getOrderDetails(orderId: number): void {
    this.loading = true;

    this.route.queryParams.subscribe((params: Params) => {
      this.orderDetailsFilters = new PaginationFilters();
      this.orderDetailsFilters.page = params['page'] ? Number(params['page']) : 1;
      this.orderDetailsFilters.size = params['size'] ? Number(params['size']) : 20;

      this.orderDetails.currentPage = this.orderDetailsFilters.page || 1;
      this.orderDetails.size = this.orderDetailsFilters.size || 20;

      const pagination = this.orderDetails.buildQuery(this.orderDetails.currentPage);

      this.graphQLService
        .getData(OrderQueries.GET_ORDER_DETAILS, {
          orderId: orderId,
          ...pagination
        })
        .pipe(
          map((res: any) => {
            const orderDetails = res.data.orderDetails;

            const paginable = new Paginable<Selectable<OrderDetail>>({
              items: orderDetails.nodes.map((csd: any) => {
                return {
                  ...this.orderMapper.toOrderDetail(csd),
                  selected: false,
                  opened: true
                };
              })
            });

            paginable.totalCount = orderDetails.totalCount;
            paginable.currentPage = this.orderDetailsFilters.page || 1;
            paginable.size = this.orderDetailsFilters.size || 2;

            return paginable;
          })
        )
        .subscribe({
          next: (data) => {
            this.orderDetails = data;

            this.getOrderResume(Number(this.order?.id));
          },
          error: (err) => {
            this.utilitiesService.showSnackbar('Error during fetching order details', SnackbarType.Error);
            console.error(err);
            this.loading = false;
          }
        });
    });
  }

  getOrderResume(_orderId: number) {
    this.loading = true;
    this.graphQLService
      .getData(OrderQueries.GET_ORDER_RESUME, { orderId: _orderId })
      .pipe(map((res: any) => this.resumeMapper.toShotResume(res.data.orderShotsResume)))
      .subscribe({
        next: (data: ShotResume) => {
          this.shotResume = data;
          this.loading = false;
        },
        error: (err) => {
          this.utilitiesService.showSnackbar('Error during fetching order resume', SnackbarType.Error);

          this.loading = false;
        }
      });
  }

  getSelected() {
    return this.orderDetails.items.filter((d) => d.selected).length || 0;
  }
  getAllSelected() {
    return this.orderDetails.items.every((d) => d.selected);
  }
  toggleSelect() {
    const allSelected = this.getAllSelected();
    this.orderDetails.items.forEach((d) => (d.selected = !allSelected));
  }

  getShotCount(product: OrderDetail, status: ShotApprovalStatus) {
    let count = 0;
    this.orderDetails.items.forEach((d) => {
      if (d.style?.modelId !== product.style?.modelId) return;
      d.colorShots?.forEach((csg) => {
        csg.shots?.forEach((shot) => {
          if (shot.shotApprovalStatus == status) {
            count++;
          }
        });
      });
    });
    return count;
  }

  someShotsAreUploaded() {
    return this.shotResume?.uploadedShots > 0;
  }

  someShotsAreDownloadable() {
    return (
      this.shotResume?.uploadedShots > 0 || this.shotResume?.approvedShots > 0 || this.shotResume?.rejectedShots > 0
    );
  }

  onFileSelected(event: Event, shot: Shot, colorId: number, modelId: number) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    const file: File = input.files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const preview = reader.result as string;

      this.openPreviewModal(file, preview, shot, colorId, modelId);
    };
    reader.readAsDataURL(file);
  }

  openPreviewModal(file: File, preview: string, shot: Shot, colorId: number, modelId: number) {
    this.dialog
      .open(ImagePreviewModalComponent, {
        data: { file: file, preview: preview },
        width: '500px'
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          shot.file = file;
          this.uploadSingleShot(shot, colorId, modelId);
        }
      });
  }

  uploadSingleShot(shot: Shot, colorId: number, modelId: number) {
    this.loading = true;

    const formData = new FormData();
    formData.append('files', shot.file as Blob);

    let shotData = {
      aspectRatioId: shot?.aspectRatio?.id,
      mediaFormatId: shot?.mediaFormat?.id,
      colorId: colorId,
      modelId: modelId,
      shotId: shot?.id,
      shotTypeId: shot?.shotType?.id
    };

    formData.append('shots', JSON.stringify([shotData]));

    if (this.order?.status?.id == OrderStatus.ReadyForPickup || this.order?.status?.id == OrderStatus.Rejected) {
      this.setOrderInProgress().subscribe({
        next: (res) => {
          if (this.order && this.order.status) {
            this.order.status.id = OrderStatus.InProgress;
          }
          if (shot) {
            this.upload(shot, formData);
          }
        },
        error: (err) => {
          this.loading = false;
          this.utilitiesService.showSnackbar(
            err.networkError.error.errors[0].message || 'Error during setting order in progress',
            SnackbarType.Error
          );
          console.error(err);
        }
      });
    } else {
      if (shot) {
        this.upload(shot, formData);
      }
    }
  }

  upload(shot: Shot, formData: FormData) {
    this.commonService.uploadFiles(formData, 'photographer').subscribe({
      next: (res) => {
        this.loading = false;
        this.orderDetails.items.forEach((d) => {
          d.colorShots?.forEach((csg) => {
            csg.shots?.forEach((s) => {
              if (shot && s.id === shot.id) {
                s.shotApprovalStatus = ShotApprovalStatus.Uploaded;
                s.imgPath = URL.createObjectURL(formData.get('files') as Blob);
              }
            });
          });
        });

        this.getOrderResume(Number(this.order?.id));

        this.utilitiesService.showSnackbar('Shot uploaded successfully', SnackbarType.Success);
      },
      error: (err) => {
        this.loading = false;
        //this.utilitiesService.showSnackbar(err.networkError.error.errors[0].message || 'Error during shot upload', SnackbarType.Error);
      }
    });
  }

  downloadAllShots() {
    this.loading = true;
    if (this.order?.id) {
      this.imageService.downloadImagesByOrderIds([this.order.id]).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          window.open(url, '_blank');
        },
        error: (err) => {
          this.loading = false;
          //this.utilitiesService.showSnackbar('Failed to start download', SnackbarType.Error);
        }
      });
    }
  }

  eraseShot(shot: Shot) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Reset Shot',
          message: 'Are you sure you want to reset this shot?'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.loading = true;
          this.graphQLService
            .postData(MutationQuery.DELETE_SHOT_IMAGE, { deleteShotImageInput: { shotId: shot.id } })
            .subscribe({
              next: (res) => {
                this.loading = false;
                shot.shotApprovalStatus = ShotApprovalStatus.NotUploaded;
                shot.imgPath = '';

                this.getOrderResume(Number(this.order?.id));

                this.utilitiesService.showSnackbar('Shot image deleted successfully', SnackbarType.Success);
              },
              error: (err) => {
                this.loading = false;
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during shot image deletion',
                  SnackbarType.Error
                );
              }
            });
        }
      });
  }

  removeAllShots() {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Clear Shots',
          message: 'Are you sure? This action will remove only not confirmed images.'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.loading = true;
          this.graphQLService
            .getData(OrderQueries.DELETE_IMAGES_BY_ORDER_ID, { OrderId: this.order?.id })

            .subscribe({
              next: (data: any) => {
                this.utilitiesService.showSnackbar('Shots removed successfully', SnackbarType.Success);
                this.loading = false;
                this.ngOnInit();
              },
              error: (err) => {
                console.log(err.networkError.error.errors[0].message);
                this.utilitiesService.showSnackbar(
                  err.networkError.error.errors[0].message || 'Error during removing shots',
                  SnackbarType.Error
                );

                this.loading = false;
              }
            });
        }
      });
  }

  eraseProductShots(product: OrderDetail[]) {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Remove shots',
          message: 'Are you sure you want to remove shots for selected products?'
        }
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          //prendi gli shots che hanno un'immagine
          const shotIds: number[] = [];
          product.forEach((p: OrderDetail) => {
            p.colorShots?.forEach((csg) => {
              csg.shots?.forEach((s) => {
                if (s.shotApprovalStatus === ShotApprovalStatus.Uploaded) {
                  if (s.id) shotIds.push(s.id);
                }
              });
            });
          });

          if (shotIds.length === 0) {
            this.utilitiesService.showSnackbar('No uploaded shots to remove for selected products', SnackbarType.Info);
            return;
          }

          this.loading = true;

          forkJoin(
            shotIds.map((shotId) =>
              this.graphQLService.postData(MutationQuery.DELETE_SHOT_IMAGE, {
                deleteShotImageInput: { shotId: shotId }
              })
            )
          ).subscribe({
            next: (res) => {
              this.loading = false;
              product.forEach((p: OrderDetail) => {
                p.colorShots?.forEach((csg) => {
                  csg.shots?.forEach((s) => {
                    s.shotApprovalStatus = ShotApprovalStatus.NotUploaded;
                    s.imgPath = '';
                  });
                });
              });

              this.utilitiesService.showSnackbar('Shots removed successfully', SnackbarType.Success);
            },
            error: (err) => {
              this.loading = false;
              this.utilitiesService.showSnackbar(
                err.networkError.error.errors[0].message || 'Error during shot image deletion',
                SnackbarType.Error
              );
            }
          });
        }
      });
  }

  /*prepareUploadMultiple() {
		
		this.graphQLService
			.getData<any>(OrderQueries.GET_ORDER_FILENAMES, {
				orderId: this.order?.id
			})

			.subscribe({
				next: (data: any) => {
					this.loading = false;

					console.log(data);

					const _estimatedCodes = data.data.orderFilenames as string[];

					this.dialog
						.open(UploadMultipleModalComponent, {
							data: {
								estimatedCodes: _estimatedCodes
							}
						})
						.afterClosed()
						.subscribe((res: UploadedImageShot[]) => {
							if (res) {
								this.loading = true;

								const formData = new FormData();

								let _shotData: any[] = [];

								res.forEach((uploadedShot: UploadedImageShot) => {
									const t = this.findShotByEstimatedFileName(uploadedShot.fileName || '');

									let shotData = {
										aspectRatioId: t?.shot?.aspectRatio?.id,
										mediaFormatId: t?.shot?.mediaFormat?.id,
										colorId: t?.colorId,
										modelId: t?.modelId,
										shotId: t?.shot?.id,
										shotTypeId: t?.shot?.shotType?.id,
										estimatedFileName: t?.shot?.estimatedFileName
									};

									_shotData.push(shotData);
									formData.append('files', uploadedShot.file as Blob);
								});

								formData.append('shots', JSON.stringify(_shotData));

								if (this.order?.status?.id == OrderStatus.ReadyForPickup || this.order?.status?.id == OrderStatus.Rejected) {
									this.setOrderInProgress().subscribe({
										next: (a) => {
											if (this.order && this.order.status) {
												this.order.status.id = OrderStatus.InProgress;
											}
											this.uploadMultiple(formData, res);
										},
										error: (err) => {
											this.loading = false;
											this.utilitiesService.showSnackbar(err.networkError.error.errors[0].message || 'Error during setting order in progress', SnackbarType.Error);
										}
									});
								} else {
									this.uploadMultiple(formData, res);
								}
							}
						});
				}
			});
	}*/

  prepareUploadMultipleWithOrchestrator() {
    this.loading = true;

    this.graphQLService
      .getData<any>(OrderQueries.GET_ORDER_FILENAMES, {
        orderId: this.order?.id
      })

      .subscribe({
        next: (data: any) => {
          this.loading = false;
          this.dialog
            .open(UploadMultipleWithOrchestratorModalComponent, {
              data: {
                order: this.order,
                estimatedCodes: data.data.orderFilenames as string[]
              }
            })
            .afterClosed()
            .subscribe((res: any) => {
              if (res) {
                this.dialog
                  .open(UploadValidationResultsModalComponent, {
                    data: {
                      instanceId: res?.instanceId
                    }
                  })
                  .afterClosed()
                  .subscribe(() => {
                    this.utilitiesService.showSnackbar('Upload completed', SnackbarType.Success);
                    this.ngOnInit();
                  });
              }
            });
        },
        error: (err) => {
          this.loading = false;
          this.utilitiesService.showSnackbar(
            err.networkError.error.errors[0].message || 'Error during fetching order filenames',
            SnackbarType.Error
          );
        }
      });
  }

  uploadMultiple(formData: FormData, data: UploadedImageShot[]) {
    this.commonService.uploadFiles(formData, 'photographer').subscribe({
      next: (r: string[]) => {
        this.loading = false;

        //aggiorno gli shot con i nuovi path facendo l'uguaglianza con extimatedFileName
        data.forEach((uploadedShot: UploadedImageShot) => {
          this.orderDetails.items.forEach((d) => {
            d.colorShots?.forEach((csg) => {
              csg.shots?.forEach((s) => {
                if (s.estimatedFileName === uploadedShot.fileName) {
                  s.shotApprovalStatus = ShotApprovalStatus.Uploaded;
                  s.imgPath = URL.createObjectURL(uploadedShot.file as Blob);
                }
              });
            });
          });
        });

        this.getOrderResume(Number(this.order?.id));

        this.utilitiesService.showSnackbar('Shot uploaded successfully', SnackbarType.Success);
      },
      error: (err) => {
        this.loading = false;
        //this.utilitiesService.showSnackbar('Error during shot upload', SnackbarType.Error);
      }
    });
  }

  findShotByEstimatedFileName(fileName: string): { colorId?: number; modelId?: number; shot?: Shot } | null {
    const normalize = (value: string) =>
      value
        .trim()
        .toLowerCase()
        .replace(/\.jpeg$/i, '.jpg');

    const normalizedFileName = normalize(fileName);

    for (let d of this.orderDetails.items) {
      for (let csg of d.colorShots || []) {
        for (let s of csg.shots || []) {
          if (normalize(s.estimatedFileName || '') === normalizedFileName) {
            let shot = s;
            shot.estimatedFileName = fileName;

            return {
              colorId: csg.color?.id,
              modelId: d.style?.modelId,
              shot: shot
            };
          }
        }
      }
    }

    return null;
  }

  viewGallery(shots: Shot[], first?: number /*id del primo shot da visualizzare */): void {
    this.dialog
      .open(ImageGalleryComponent, {
        data: { shots: shots, first: first }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          // handle result if needed
        }
      });
  }

  getShotsWithImages(colorShots: ColorShots): Shot[] {
    return colorShots.shots?.filter((s) => s.imgPath) || [];
  }

  getIds(shots: Shot[]): number[] {
    return shots.map((s) => s.id!);
  }

  downloadShots(shotIds: number[]): void {
    this.loading = true;
    this.imageService.downloadImagesByShotIds(shotIds).subscribe({
      next: (res: Blob) => {
        this.loading = false;
        const url = window.URL.createObjectURL(res);
        window.open(url, '_blank');
        this.utilitiesService.showSnackbar('Download started', SnackbarType.Success);
      },
      error: (err) => {
        this.loading = false;
        //this.utilitiesService.showSnackbar('Failed to start download', SnackbarType.Error);
      }
    });
  }

  forbiddenEdit() {
    return (
      this.order?.status?.id == OrderStatus.ShootCompleted ||
      this.order?.status?.id == OrderStatus.Approved ||
      this.order?.status?.id == OrderStatus.Submitted
    );
  }

  //questo metodo mette in progress l'ordine , e lo faccio prima del primo upload
  setOrderInProgress() {
    let payload = {
      orderId: this.order?.id,
      orderStatusId: OrderStatus.InProgress
    };

    return this.graphQLService.postData(MutationQuery.UPDATE_ORDER_STATUS, { updateOrderStatusInput: payload });
  }

  downloadFilenamesExcel() {
    if (this.order && this.order.id) {
      this.loading = true;
      this.excelService.downloadFilenamesExcel(this.order.id).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Filenames_Order_${this.order?.id}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.utilitiesService.showSnackbar('Excel exported successfully', SnackbarType.Success);
        },
        error: (err) => {
          this.loading = false;
          //this.utilitiesService.showSnackbar('Failed to export excel', SnackbarType.Error);
        }
      });
    }
  }

  downloadShootingList() {
    if (this.order && this.order.id) {
      this.loading = true;
      this.excelService.downloadShootingList(this.order.id).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `ShootingList_Order_${this.order?.id}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.utilitiesService.showSnackbar('Excel exported successfully', SnackbarType.Success);
        },
        error: (err) => {
          this.loading = false;
          //this.utilitiesService.showSnackbar('Failed to export excel', SnackbarType.Error);
        }
      });
    }
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(
      () => {
        this.utilitiesService.showSnackbar('Filename copied to clipboard', SnackbarType.Success);
      },
      (err) => {
        this.utilitiesService.showSnackbar('Failed to copy filename', SnackbarType.Error);
      }
    );
  }

  sendToBasicNet() {
    this.dialog
      .open(ConfirmModalComponent, {
        data: {
          title: 'Send shots to BasicNet',
          message: 'Are you sure you want to send this order to BasicNet?'
        }
      })
      .afterClosed()
      .subscribe({
        next: (res) => {
          if (res) {
            let payload = {
              orderId: this.order?.id,
              orderStatusId: OrderStatus.ShootCompleted
            };

            this.loading = true;
            this.graphQLService
              .postData(MutationQuery.UPDATE_ORDER_STATUS, { updateOrderStatusInput: payload })
              .subscribe({
                next: (value: any) => {
                  if (this.order && this.order.status) {
                    this.order.status.id = OrderStatus.ShootCompleted;
                  }
                  this.loading = false;
                  this.ngOnInit();

                  this.utilitiesService.showSnackbar('Shots sent to BasicNet successfully', SnackbarType.Success);
                },
                error: (err) => {
                  this.loading = false;
                  this.utilitiesService.showSnackbar(
                    err.networkError.error.errors[0].message || 'Error during setting order in progress',
                    SnackbarType.Error
                  );
                }
              });
          }
        }
      });
  }

  thereAreImages(): boolean {
    return this.shotResume.uploadedShots > 0 || this.shotResume.approvedShots > 0 || this.shotResume.rejectedShots > 0;
  }

  isVideo(shot: Shot): boolean {
    if (shot.imgPath) {
      let path = shot.imgPath.toLowerCase();
      if (path.lastIndexOf('.mp4') > 0 || path.lastIndexOf('.mkv') > 0) return true;
    }
    return false;
  }
}
